import { createStore } from "zustand/vanilla";
import { Services } from "@/types/serviceTypes";
import { emitServices } from "@/utils/services/emitServices";

type SharedServicesState = {
  currentVariantId: string | undefined;
  availableServices: Services;
  syncForVariant(variantId: string, incomingServices: Services): void;
  selectService(serviceId: string): void;
  removeService(serviceId: string): void;
  reset(): void;
};

export const sharedServicesStore = createStore<SharedServicesState>(
  (set, get) => ({
    currentVariantId: undefined,
    availableServices: [],

    syncForVariant(variantId, incomingServices) {
      const { currentVariantId, availableServices } = get();

      const processed = incomingServices.map((s) =>
        s.isMandatory ? { ...s, selected: true } : s,
      );

      if (currentVariantId === variantId && availableServices.length > 0) {
        const hasMandatoryNotSelected = availableServices.some(
          (s) => s.isMandatory && !s.selected,
        );
        if (hasMandatoryNotSelected) {
          const next = availableServices.map((s) =>
            s.isMandatory ? { ...s, selected: true } : s,
          );
          set({ availableServices: next });
          emitServices(next.filter((s) => s.selected));
        }
        return;
      }

      const isVariantChange =
        currentVariantId !== undefined && currentVariantId !== variantId;

      set({ currentVariantId: variantId, availableServices: processed });
      const selected = processed.filter((s) => s.selected);

      if (selected.length > 0 || isVariantChange) {
        emitServices(selected);
      }
    },

    selectService(serviceId) {
      const { availableServices } = get();
      if (!availableServices.find((s) => s.id === serviceId)) return;
      const next = availableServices.map((s) =>
        s.id === serviceId ? { ...s, selected: true } : s,
      );
      set({ availableServices: next });
      emitServices(next.filter((s) => s.selected));
    },

    removeService(serviceId) {
      const { availableServices } = get();
      const service = availableServices.find((s) => s.id === serviceId);
      if (!service || service.isMandatory || !service.selected) return;
      const next = availableServices.map((s) =>
        s.id === serviceId ? { ...s, selected: false } : s,
      );
      set({ availableServices: next });
      emitServices(next.filter((s) => s.selected));
    },

    reset() {
      set({ currentVariantId: undefined, availableServices: [] });
    },
  }),
);

if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (event: PageTransitionEvent) => {
    if (event.persisted) {
      const { availableServices } = sharedServicesStore.getState();
      const selected = availableServices.filter((s) => s.selected);
      if (selected.length > 0) {
        emitServices(selected);
      }
    }
  });
}
